import { colors } from '../../../../themes/theme'
import { TranslationMap } from '../../../../types/TranslationMap'

// ============================================================================
// Color Theme
// ============================================================================

export const COLORS = {
  positive: colors.success,
  negative: colors.warning,
  neutral: colors.subtle,
  trackBg: colors.lightGrayBackground,
  centerLine: colors.divider,
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Formats a percentage change value with sign prefix.
 * @param percentChange - The percentage change value
 * @returns Formatted string like "+2.1%", "-0.5%", or "0%"
 */
export const formatPercentChange = (percentChange: number | null): string => {
  if (percentChange === null) return '—'
  if (percentChange === 0) return '0%'
  const sign = percentChange > 0 ? '+' : ''
  return `${sign}${percentChange.toFixed(1)}%`
}

/**
 * Formats a percentage as a compact integer for display next to the bar.
 * e.g., +2.3% becomes "+2", -15.7% becomes "-16"
 */
export const formatCompactPercent = (percentChange: number | null): string => {
  if (percentChange === null) return '—'
  if (percentChange === 0) return '0'
  const sign = percentChange > 0 ? '+' : ''
  return `${sign}${Math.round(percentChange)}`
}

/**
 * Formats a number with thousand separators
 */
export const formatNumber = (value: number | null): string => {
  if (value === null) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

// ============================================================================
// Bar Calculation Functions
// ============================================================================

/**
 * Gets the bar color based on the percent change value.
 */
export const getBarColor = (percentChange: number | null): string => {
  if (percentChange === null || percentChange === 0) return COLORS.neutral
  return percentChange > 0 ? COLORS.positive : COLORS.negative
}

/**
 * Calculates the bar width as a percentage of the track half.
 * Uses logarithmic-ish scaling so small values are still visible.
 *
 * Scale:
 * - 0%: 0% width
 * - 1-5%: 15-25% width (minimum visible)
 * - 5-20%: 25-40% width
 * - 20-50%: 40-50% width
 * - 50%+: 50% width (max, fills half the track)
 */
export const getBarWidth = (percentChange: number | null): number => {
  if (percentChange === null || percentChange === 0) return 0
  const absValue = Math.abs(percentChange)

  // Minimum visible width for any non-zero value
  const minWidth = 15

  // Scale: use square root for better small value visibility
  // sqrt(50) ≈ 7.07, so we scale to fill 50% at ±50% change
  const scaledWidth = Math.sqrt(absValue) * (50 / Math.sqrt(50))

  // Clamp between minWidth and 50%
  return Math.max(minWidth, Math.min(50, scaledWidth))
}

// ============================================================================
// Translation Helper
// ============================================================================

/**
 * Gets the translated name from a TranslationMap based on user language.
 * Falls back to English if the user's language is not available.
 */
export const getTranslatedName = (translationMap: TranslationMap, userLanguage: string): string => {
  const lang = userLanguage as keyof TranslationMap
  return translationMap[lang] || translationMap['en'] || ''
}
